/**
 * services/api.ts - slim barrel.
 *
 * Per ADR-0003 the per-domain API modules under ./api/ now hold the canonical
 * implementations of every *Api object. This file used to be a 9,000+ LOC
 * monolith with duplicate copies of those same exports; the trim on 2026-04-30
 * replaced the duplicates with re-exports while preserving every public symbol
 * name and signature so existing component imports (e.g.
 * `import { cspmApi } from '@/services/api'`) continue to work unchanged.
 *
 * Follow-up on 2026-04-30 (this commit): the remaining 27 LOCAL ONLY symbols
 * were extracted into 7 new per-domain modules under ./api/:
 *   - findings.ts            findingsApi
 *   - compliance.ts          complianceApi
 *   - policy-management.ts   customPolicyApi, policyVersionApi, regoPolicyApi,
 *                            policyBundlesApi, iacPolicyScanApi
 *   - audit-siem.ts          siemApi, auditChainApi, auditRetentionApi
 *   - cspm-extras.ts         cspmOverviewApi, driftDetectorApi,
 *                            exceptionServiceApi, orgScannerApi,
 *                            cspmFindingsExtApi, cspmScanExtApi,
 *                            cspmPolicyMgmtApi
 *   - observability.ts       costApi, platformHealthApi, mttrApi,
 *                            automationRulesApi
 *   - integrations-extras.ts dataLakeApi, eventStreamApi2,
 *                            thirdPartyIngestionApi, telemetryGapApi,
 *                            detectionRuleLibraryApi, boardsApi
 *
 * This file is now a pure re-export barrel. New code should import directly
 * from the per-domain module under ./api/<domain>; this barrel exists only
 * for backwards compatibility with existing component imports.
 */
import { API_CONFIG } from './api/config';
import { apiClient as fetchClient } from './api/client';

// API_CONFIG.BASE_URL is read by fetchClient itself; reference kept to silence
// unused-import warnings for callers that inspect this barrel.
void API_CONFIG;

// ============================================================================
// Per-domain re-exports (canonical implementations live under ./api/)
// ============================================================================

export { findingsApi } from './api/findings';
export { complianceApi } from './api/compliance';
export { customPolicyApi, policyVersionApi, regoPolicyApi, policyBundlesApi, iacPolicyScanApi } from './api/policy-management';
export { siemApi, auditChainApi, auditRetentionApi } from './api/audit-siem';
export { cspmOverviewApi, driftDetectorApi, exceptionServiceApi, orgScannerApi, cspmFindingsExtApi, cspmScanExtApi, cspmPolicyMgmtApi } from './api/cspm-extras';
export { costApi, platformHealthApi, mttrApi, automationRulesApi } from './api/observability';
export { dataLakeApi, eventStreamApi2, thirdPartyIngestionApi, telemetryGapApi, detectionRuleLibraryApi, boardsApi } from './api/integrations-extras';

export { cloudAccountsApi, resourcesApi, remediationServiceApi, notificationServiceApi, dashboardApi, inventoryApi, rbacApi, analyticsApi, workflowsApi, adminApi, projectsApi, reportSchedulesApi, autoRemediationApi, ssoApi, mfaApi, alertPipelineApi, oauthApi, apiKeysApi, ssoGroupMappingApi, adminUsersApi, adminSettingsApi, adminBackupsApi, adminEncryptionApi, adminAuditApi, adminWorkflowsApi, adminCustomRolesApi, ssoProvidersApi, mfaManagementApi, adminSiemApi, mfaStatusApi, tenantApi, customDashboardsApi, tenantsApi, billingApi } from './api/platform';
export { cwppApi, kspmApi, kspmClusterApi, snapshotScanApi, imageDriftApi, runtimeSensorApi } from './api/cwpp';
export { ciemApi, crossCloudApi, ciemRightsizingApi } from './api/ciem';
export { dspmApi, dataResidencyApi, dspmContentApi, aiTrainingDataApi } from './api/dspm';
export { cdrApi, realtimeCSPMApi, siemExportApi, eventStreamApi, cdrLogAnalyticsApi, cdrAdvancedApi, threatIntelFeedsApi, preventionApi, forensicTimelineApi, investigationApi } from './api/cdr';
export { containerSecurityApi } from './api/container';
export { securityGraphApi, graphResourceApi, graphqlApi, graphEventsApi, saasGraphApi, graphRiskApi, graphBuildJobsApi, graphOpsApi, graphDlqApi, graphVersionsApi, toxicComboRulesApi } from './api/graph';
export { cicdScannerApi, pipelinesApi, supplyChainApi, scaApi, codeSecurityApi, sbomApi, gitopsApi, codeSecurityTriageApi } from './api/code-security';
export { easmApi, exposureApi, exposureWindowApi, networkReachabilityApi } from './api/easm';
export { aiSecurityApi, aiAssistantApi, aiSpmApi } from './api/ai';
export { serviceNowApi, slackBotApi, ticketSyncApi, jiraSyncApi, integrationHubApi } from './api/integrations';
export { vulnManagementApi, virtualPatchApi } from './api/vulnerability';
export { eventScannerApi } from './api/event-scanner';

// Default export retained for backwards compat: re-export the canonical fetch client.
export default fetchClient;
